import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, ClipboardList, Users as UsersIcon, Plus, TrendingUp, Package,
  ChevronRight, AlertCircle, Euro,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { useLang } from "../../context/LangContext";
import { API, useFmt, statusLabelMap, STATUS_COLOR } from "./_shared";

const KPI = ({ label, value, sub, icon: Icon, accent = "text-[#8A1C2A]" }) => (
  <Card className="bg-white border-stone-200 p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-stone-500 uppercase tracking-wide">{label}</p>
        <p className={`text-2xl sm:text-3xl font-bold mt-2 ${accent}`}>{value}</p>
        {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
      </div>
      <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-stone-300" />
    </div>
  </Card>
);

const Dashboard = ({ onPendingChanged }) => {
  const { t } = useLang();
  const fm = useFmt();
  const [data, setData] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/agent/dashboard`)
      .then((r) => setData(r.data))
      .catch(() => toast.error(t.pAgent_loadError));
  }, [t.pAgent_loadError]);
  if (!data) return <div className="p-6"><div className="spinner w-6 h-6" /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">{t.dashboard}</h1>
          <p className="text-stone-500 text-sm mt-1">{t.pAgent_overview}</p>
        </div>
        <Link to="/agent/new-order" className="inline-flex items-center gap-2 px-4 py-2 bg-[#8A1C2A] hover:bg-[#6B1420] text-white rounded-md text-sm font-semibold" data-testid="dashboard-new-order-btn">
          <Plus className="w-4 h-4" /> {t.pAgent_newOrder}
        </Link>
      </div>

      {(data.pending_approval_count > 0) && (
        <Link to="/agent/approvals" className="block">
          <Card className="bg-amber-50 border-amber-200 p-4 hover:bg-amber-100 transition-colors" data-testid="pending-approval-banner">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900">{data.pending_approval_count} ordine{data.pending_approval_count > 1 ? "i" : ""} da approvare</p>
                <p className="text-xs text-amber-700">I tuoi clienti hanno inviato ordini che attendono la tua revisione e approvazione.</p>
              </div>
              <ChevronRight className="w-5 h-5 text-amber-600" />
            </div>
          </Card>
        </Link>
      )}

      <div>
        <h2 className="text-sm font-semibold text-stone-700 mb-3 uppercase tracking-wide">📊 {t.commission} — {t.biPeriodThisMonth}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPI label={t.pAgent_kpiCommissionEarned} value={`${fm(data.month_commission_earned)}`} sub={t.pAgent_kpiCommissionEarnedSub} icon={TrendingUp} accent="text-[#C5A059]" />
          <KPI label={t.pAgent_kpiCommissionCollected} value={`${fm(data.month_commission_collected)}`} sub={t.pAgent_kpiCommissionCollectedSub} icon={TrendingUp} accent="text-green-700" />
          <KPI label={t.pAgent_monthOrders} value={data.month_orders} sub={`${fm(data.month_revenue)} ${t.pAgent_monthRevenueSub}`} icon={ClipboardList} />
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-stone-700 mb-3 uppercase tracking-wide">📈 {t.overview}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <KPI label={t.pAgent_kpiTotalRevenue} value={`${fm(data.total_revenue)}`} sub={`${data.total_orders} ${t.orders}`} icon={Euro} accent="text-stone-900" />
          <KPI label={t.pAgent_kpiCommissionEarned} value={`${fm(data.total_commission_earned)}`} icon={TrendingUp} accent="text-[#C5A059]" />
          <KPI label={t.pAgent_kpiCommissionCollected} value={`${fm(data.total_commission_collected)}`} icon={TrendingUp} accent="text-green-700" />
          <KPI label={t.pAgent_kpiCommissionPending} value={`${fm(data.total_commission_pending)}`} sub={t.pAgent_subTotalPending} icon={Package} accent="text-stone-500" />
          <KPI label={t.myCustomers} value={data.customers_count} sub={t.customers} icon={UsersIcon} />
        </div>
      </div>

      <Card className="bg-white border-stone-200 overflow-hidden">
        <div className="p-4 border-b border-stone-200 flex items-center justify-between">
          <h3 className="font-semibold text-stone-900">{t.pAgent_lastOrders}</h3>
          <Link to="/agent/orders" className="text-sm text-[#8A1C2A] hover:underline">{t.all} →</Link>
        </div>
        {data.recent_orders.length === 0 ? (
          <div className="p-8 text-center text-stone-500">Nessun ordine</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left p-3 text-xs font-semibold text-stone-600">N° Ordine</th>
                  <th className="text-left p-3 text-xs font-semibold text-stone-600">Cliente</th>
                  <th className="text-left p-3 text-xs font-semibold text-stone-600">Data</th>
                  <th className="text-right p-3 text-xs font-semibold text-stone-600">Totale</th>
                  <th className="text-right p-3 text-xs font-semibold text-stone-600">Provv.</th>
                  <th className="text-center p-3 text-xs font-semibold text-stone-600">Stato</th>
                  <th className="text-center p-3 text-xs font-semibold text-stone-600">Pagam.</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_orders.map((o) => (
                  <tr key={o.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="p-3 font-mono text-xs font-semibold text-[#8A1C2A]">{o.order_number}</td>
                    <td className="p-3">{o.customer?.customer_name || o.customer?.company}</td>
                    <td className="p-3 text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="p-3 text-right font-semibold">{fm(((o.final_grand_total ?? o.final_total_amount ?? o.total_amount) || 0))}</td>
                    <td className="p-3 text-right text-[#C5A059] font-semibold">{fm(((o.final_commission_amount ?? o.commission_amount) || 0))}</td>
                    <td className="p-3 text-center"><Badge className={STATUS_COLOR[o.status] || "bg-stone-100"}>{statusLabelMap(t)[o.status] || o.status}</Badge></td>
                    <td className="p-3 text-center">
                      {o.is_fully_paid ? <Badge className="bg-green-100 text-green-700">Saldato</Badge> : ((o.paid_amount || 0) > 0 ? <Badge className="bg-orange-100 text-orange-700">Parziale</Badge> : <Badge className="bg-stone-100 text-stone-600">Da saldare</Badge>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
